import React from 'react';
import { useTranslation } from 'react-i18next';
import TitleWithIcon from 'Components/Molecules/TitleWithIcon';
import { VerifiedUserOutlined } from '@material-ui/icons';
import Select from 'Components/Atoms/Select';
import Typography from 'Components/Atoms/Typography';
import useDocument from 'CustomHooks/useDocument';
import useRestaurant from 'CustomHooks/useRestaurant';
import { NoShowAppSettings } from 'types/noShowProtection';

export default function NoShowTemplateSelect({ value, shiftId, onChange, readOnly = false }: {
  value: string | null | undefined;
  shiftId?: string;
  onChange?: (value: string | null) => void;
  readOnly?: boolean;
}) {
  const { restaurantId } = useRestaurant();
  const { t } = useTranslation('noShowProtection');
  const [app] = useDocument<{ active?: boolean; settings?: NoShowAppSettings }>(
    `restaurants/${restaurantId}/apps`, 'noShowMollie'
  );
  if (!app.data?.active) return null;
  const templates = (app.data.settings?.templates ?? []).filter((template) => template.active);
  const selected = value === undefined
    ? templates.find((template) => template.scope === 'shifts' && template.shiftIds?.includes(shiftId ?? ''))?.id ?? ''
    : value ?? '';
  const options = [
    { id: '', label: t('Restaurant default'), translation: null },
    ...templates.map((template) => ({ id: template.id, label: template.name, translation: null })),
  ];
  if (value && !templates.some((template) => template.id === value)) {
    options.push({ id: value, label: t('Template unavailable'), translation: null });
  }
  if (readOnly) {
    const assigned = selected ? templates.filter((template) => template.id === selected)
      : templates.filter((template) => template.scope === 'restaurant');
    const label = assigned.map((template) => [
      template.name,
      template.conditions?.minGuests ? `${t('Min. guests')}: ${template.conditions.minGuests}` : null,
      template.conditions?.maxGuests ? `${t('Max. guests')}: ${template.conditions.maxGuests}` : null,
    ].filter(Boolean).join(' · ')).join('; ');
    return (
      <div className="details-row">
        <Typography variant="text-3" className="details-row-title" translation={null}>{t('No-show protection')}</Typography>
        <Typography variant="text-3" color="subdued" className="details-row-value" translation={null}>
          {label || (selected ? t('Template unavailable') : t('No template assigned'))}
        </Typography>
      </div>
    );
  }
  return (
    <TitleWithIcon
      title="No-show protection"
      titleTranslation="noShowProtection"
      description="Choose a published template. The calendar save dialog determines whether this applies only to this date or to future shifts."
      descriptionTranslation="noShowProtection"
      icon={(props) => <VerifiedUserOutlined {...props} />}
    >
      <Select
        fullWidth
        value={selected}
        options={options}
        onChange={(event) => onChange?.(String(event.target.value) || null)}
        disabled={app.loading || !!app.error}
        inputProps={{ 'aria-label': t('No-show protection') }}
      />
    </TitleWithIcon>
  );
}
